<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="installer-information-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.installerInformations.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="installer-information-page-title"
        />
      </div>

      <InstallerForm ref="installerFormRef" :disabled="isFormDisabled" />
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useStepStore, useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import type { SubscriptionInstallation } from '@/types/subscription.type';
import { getErrorToasterConfig } from '@/utils/error.util';

import InstallerForm from '@/components/subscription/InstallerForm.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { EInstallationPerformer } from '@/constants/subscription.constants';

const { t } = useI18n();

const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();

const { goToNextStep } = stepStore;

const { isSaveSubscriptionInProgress, isGetSubscriptionInProgress } =
  storeToRefs(subscriptionStore);

const installerFormRef = ref<InstanceType<typeof InstallerForm> | null>(null);

const isFormDisabled = computed(
  () => isGetSubscriptionInProgress.value || isSaveSubscriptionInProgress.value
);

const onSubmit = async () => {
  toastSnackBar.clearToasts();

  const { valid } = await installerFormRef.value!.validate();

  if (!valid) {
    return;
  }

  const payload: SubscriptionInstallation = {
    professionalInstaller: installerFormRef.value!.getValues(),
    installationPerformedBy: EInstallationPerformer.PROFESSIONAL_INSTALLER,
  };

  try {
    await subscriptionStore.updateSubscriptionWithInstallation(payload);

    goToNextStep();
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'installer-information-error',
      defaultMessage: t(
        'subscription.installerInformations.api.errors.patchSubscriptionInstallation'
      ),
    });

    toastSnackBar.addToast(toastConfig);
  }
};
</script>

<style scoped></style>
